import { ReactNode, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { CollectionPointSearchParams } from '../../../../../types/collectionPoints';
import { LOADING_TYPES } from '../../../../../utils/checkoutState';
import { config } from '../../../../../utils/config';
import { getSearchParamsFromPlace } from '../../../../../utils/googleMaps';
import InputWithIconAndButton from '../../../../atoms/InputWithIconAndButton';
import { showNotification } from '../../../Notifications';
import Filters, { FilterOption } from '../Filters';
import './style.scss';

// There is no "load" event coming from google maps places autocomplete so we need to implement our own waiting logic
// checking for the class name change the library performs on the input element
const waitForAutocompleteReady = (inputElement: HTMLInputElement) =>
  new Promise<void>(resolve => {
    const observer = new MutationObserver(mutationList => {
      if (mutationList.some(mutation => mutation.type === 'attributes' && mutation.attributeName === 'class')) {
        observer.disconnect();
        resolve();
      }
    });
    observer.observe(inputElement, { attributes: true });
  });

type AutocompleteSearchSectionProps = {
  onSearch: (searchParams: CollectionPointSearchParams) => Promise<void>;
  onClearSearch: () => void;
  searchParams: CollectionPointSearchParams;
  initialValues: CollectionPointSearchParams;
  isLoading: string;
  isGoogleMapsSdkLoaded: boolean;
  dropdown: ReactNode;
  availableFilters?: FilterOption[];
};

const AutocompleteSearchSection = ({ onSearch, onClearSearch, ...rest }: AutocompleteSearchSectionProps) => {
  const { initialValues, isLoading, isGoogleMapsSdkLoaded, dropdown, availableFilters, searchParams } = rest;
  const { formatMessage } = useIntl();
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState(initialValues.formattedAddress);

  useEffect(() => {
    setValue(initialValues.formattedAddress);
  }, [initialValues.formattedAddress]);

  useEffect(() => {
    if (!isGoogleMapsSdkLoaded) {
      return;
    }

    try {
      const autocomplete = new google.maps.places.Autocomplete(inputRef.current!, {
        componentRestrictions: { country: [config.country] },
        fields: ['address_components', 'geometry'],
        types: ['address']
      });
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        const _searchParams = getSearchParamsFromPlace(place);
        if (!_searchParams) {
          showNotification({
            level: 'warning',
            text: formatMessage({ id: 'shipping.notifications.incompleteAddressComponents' }),
            autoClose: true
          });
          return;
        }
        setValue(_searchParams.formattedAddress);
        onSearch(_searchParams);
      });
      (async () => {
        await waitForAutocompleteReady(inputRef.current!);
        inputRef.current!.focus({ preventScroll: true });
      })();
    } catch (e: any) {
      console.error(e);
    }
  }, [isGoogleMapsSdkLoaded]);

  const onClearInput = () => {
    if (!isLoading) {
      setValue('');
      onClearSearch();
    }
  };

  return (
    <>
      <div className="collection-point-search-section">
        <InputWithIconAndButton
          isLoading={isLoading === LOADING_TYPES.collectionPointSearch}
          placeholder={formatMessage({ id: 'generic.search' })}
          inputref={inputRef}
          value={value}
          onChange={e => setValue(e.currentTarget.value)}
          button={{ text: 'generic.clear', onClick: onClearInput }}
          data-test-id="shipping-collection-point-address"
        />
      </div>
      {!!availableFilters?.length && (
        <Filters
          availableFilters={availableFilters}
          onClick={onSearch}
          searchParams={searchParams}
          isLoading={isLoading}
        />
      )}
      {!value && isGoogleMapsSdkLoaded && dropdown}
    </>
  );
};

export default AutocompleteSearchSection;
